import React, { useContext, useState } from "react";
import { Modal } from "react-bootstrap";
import useTranslator from "../../hooks/useTranslator";
import {
  formatPropertyAddress,
  haveValue,
  toast,
  validatePhoneNumber,
} from "../../helper/common";
import LandLordContext from "../../context/landlord/LandLordContext";
import AuthContext from "../../context/auth/AuthContext";
import Cancelimg from "../../assets/img/cancel-01.svg";
import { useParams } from "react-router-dom";
import copy from "copy-to-clipboard";
import { PhoneInput } from "react-international-phone";
import "react-international-phone/style.css";
import { MdOutlineMail } from "react-icons/md";
import { FaWhatsapp } from "react-icons/fa6";

const InviteTenantModal = ({
  show,
  onHide,
  propertyId,
  property,
  onSuccess,
}) => {
  const { _t } = useTranslator();
  const { sendInvitationToTenant } = useContext(LandLordContext);
  const { user } = useContext(AuthContext);
  const { workspaceId } = useParams();

  const [activeTab, setActiveTab] = useState("email");
  const [sharerEmail, setSharerEmail] = useState("");
  const [sharerName, setSharerName] = useState("");
  const [sharerPhone, setSharerPhone] = useState("");
  const [message, setMessage] = useState("");
  const [sendInvitationLoading, setSendInvitationLoading] = useState(false);

  // Build invite link — same as workspace invite link
  let inviteLink = `${window.location.protocol}//${process.env.REACT_APP_URL_NAME}/home?requestProfileLink=${user?._id}_${workspaceId}`;
  if (haveValue(propertyId)) {
    inviteLink += `&property_id=${propertyId}`;
  }

  const handleClose = () => {
    setSharerEmail("");
    setSharerName("");
    setSharerPhone("");
    setMessage("");
    setActiveTab("email");
    onHide();
  };

  const handleCopyLink = () => {
    copy(inviteLink);
    toast.success(_t("linkCopied"));
  };

  // Property address
  const propertyObj = property || {};
  const addressLine1 = formatPropertyAddress(propertyObj);
  const city = propertyObj?.city || "";
  const postalCode = propertyObj?.postal_code || "";
  const addressLine2 = [city, postalCode].filter(Boolean).join(", ");

  // --- Email send ---
  const sendInvitationViaEmail = async (e) => {
    e.preventDefault();
    if (/^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/.test(sharerEmail)) {
      setSendInvitationLoading(true);
      try {
        const response = await sendInvitationToTenant({
          type: "email",
          name: sharerName,
          email: sharerEmail,
          message,
          property_id: propertyId,
        });
        const data = response.data;
        if (data.message) {
          toast.success(data.message);
        }
        handleClose();
        onSuccess?.();
      } catch (error) {
        const response = error.response;
        if (response?.data?.err) {
          toast.error(response.data.err);
        }
      } finally {
        setSendInvitationLoading(false);
      }
    } else {
      toast.error(_t("invalidEmailAddress"));
    }
  };

  // --- WhatsApp send ---
  const sendInvitationViaPhone = async (e) => {
    e.preventDefault();
    const validate = validatePhoneNumber(sharerPhone?.replace(/\+/g, ""));
    if (!validate.isValid) {
      toast.error(validate.message);
      return;
    }
    setSendInvitationLoading(true);
    try {
      const response = await sendInvitationToTenant({
        type: "phone",
        name: sharerName,
        phone: sharerPhone,
        message,
        property_id: propertyId,
      });
      const data = response.data;
      if (data.message) {
        toast.success(data.message);
      }
      handleClose();
      onSuccess?.();
    } catch (error) {
      const response = error.response;
      if (response?.data?.err) {
        toast.error(response.data.err);
      }
    } finally {
      setSendInvitationLoading(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      className="newmodalui wrapper-modal inviteTenantModal max-w-lg"
    >
      <Modal.Header>
        <Modal.Title>{_t("inviteTenant")}</Modal.Title>
        <div className="cursor-pointer" onClick={handleClose}>
          <img src={Cancelimg} alt="icon" className="canceicon topRightIcon" />
        </div>
      </Modal.Header>

      <Modal.Body>
        {/* ── Property info card ── */}
        {(addressLine1 || addressLine2) && (
          <div className="itm-property-card">
            <div className="itm-property-icon-wrap">
              {/* Existing building icon from /public/images, colored white on dark green bg */}
              <svg
                width="20"
                height="20"
                viewBox="0 0 18 18"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M12 7.5L13.612 7.98361C14.6419 8.29258 15.1569 8.44706 15.4534 8.84565C15.75 9.24424 15.75 9.78186 15.75 10.8571V16.5"
                  stroke="#82CF3F"
                  strokeWidth="1.2"
                  strokeLinejoin="round"
                />
                <path
                  d="M6 6.75L8.25 6.75M6 9.75L8.25 9.75"
                  stroke="#82CF3F"
                  strokeWidth="1.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M9 16.5V14.25C9 13.5429 9 13.1893 8.78033 12.9697C8.56066 12.75 8.20711 12.75 7.5 12.75H6.75C6.04289 12.75 5.68934 12.75 5.46967 12.9697C5.25 13.1893 5.25 13.5429 5.25 14.25V16.5"
                  stroke="#82CF3F"
                  strokeWidth="1.2"
                  strokeLinejoin="round"
                />
                <path
                  d="M1.5 16.5L16.5 16.5"
                  stroke="#82CF3F"
                  strokeWidth="1.2"
                  strokeLinecap="round"
                />
                <path
                  d="M2.25 16.5V5.03793C2.25 3.15487 2.25 2.21334 2.84339 1.74618C3.43677 1.27903 4.31056 1.53266 6.05814 2.03992L9.80814 3.12841C10.8627 3.43452 11.39 3.58758 11.695 4.00474C12 4.4219 12 4.99008 12 6.12642V16.5"
                  stroke="#82CF3F"
                  strokeWidth="1.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <div className="itm-property-text">
              {addressLine1 && (
                <span className="itm-property-title">{addressLine1}</span>
              )}
              {addressLine2 && (
                <span className="itm-property-sub">{addressLine2}</span>
              )}
            </div>
          </div>
        )}

        {/* ── Invite link row ── */}
        <div className="itm-link-row">
          <span className="itm-link-icon">
            {/* chain-link icon */}
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
              <path
                d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71"
                stroke="#7F7F7F"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71"
                stroke="#7F7F7F"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
          <span className="itm-link-text" title={inviteLink}>
            {inviteLink}
          </span>
          <button
            type="button"
            className="itm-copy-btn"
            onClick={handleCopyLink}
          >
            {/* copy icon */}
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
              <rect
                x="9"
                y="9"
                width="13"
                height="13"
                rx="2"
                stroke="currentColor"
                strokeWidth="2"
              />
              <path
                d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"
                stroke="currentColor"
                strokeWidth="2"
              />
            </svg>
            {_t("copyLink") || "Copy link"}
          </button>
        </div>

        {/* ── or divider ── */}
        <div className="itm-or-divider">
          <span>{_t("or") || "or"}</span>
        </div>

        {/* ── Tab toggle ── */}
        <div className="itm-tab-row">
          <button
            type="button"
            className={`itm-tab-btn${activeTab === "email" ? " active" : ""}`}
            onClick={() => setActiveTab("email")}
          >
            {/* envelope icon */}
            <MdOutlineMail size={20} />
            {_t("sendViaEmail") || "Send via email"}
          </button>
          <button
            type="button"
            className={`itm-tab-btn${activeTab === "whatsapp" ? " active" : ""}`}
            onClick={() => setActiveTab("whatsapp")}
          >
            {/* whatsapp bubble icon */}
            <FaWhatsapp size={20} />
            {_t("sendViaWhatsApp") || "Send via WhatsApp"}
          </button>
        </div>

        {/* ── Email form ── */}
        {activeTab === "email" && (
          <form onSubmit={sendInvitationViaEmail} className="itm-form">
            <div className="itm-input-row">
              <div className="forminput customInput mb-0">
                <label>{_t("tenantName")}</label>
                <input
                  type="text"
                  className="modal-input"
                  placeholder={_t("tenantName") || "Tenant name"}
                  value={sharerName}
                  required
                  onChange={(e) => setSharerName(e.target.value)}
                  onBeforeInput={(e) => {
                    const isValidChar = /^[A-Za-zÀ-ÿ\s'-]+$/.test(e.data);
                    if (!isValidChar) e.preventDefault();
                  }}
                />
              </div>
              <div className="forminput customInput mb-0">
                <label>{_t("tenantEmail")}</label>
                <input
                  type="email"
                  className="modal-input"
                  placeholder={_t("tenantEmail") || "tenant@email.com"}
                  value={sharerEmail}
                  required
                  onChange={(e) => setSharerEmail(e.target.value)}
                  onBeforeInput={(e) => {
                    if (!e.data) return;
                    const isValid = /^[a-z0-9._%+-@]+$/.test(e.data);
                    if (!isValid) {
                      e.preventDefault();
                      return;
                    }
                    const existingValue = e.target.value;
                    const totalAtCount =
                      (existingValue + e.data).split("@").length - 1;
                    if (totalAtCount > 1) e.preventDefault();
                  }}
                />
              </div>
            </div>
            <div className="forminput customInput m-0">
              <label>{_t("messageTenantOptional")}</label>
              <textarea
                className="modal-input w-100 no-textarea-focus"
                placeholder={
                  _t("messageTenantOptional") || "Message to tenant (optional)"
                }
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={4}
              />
            </div>
            <p className="itm-hint">
              {_t("messageIncludedInEmail") ||
                "This message will be included in the invitation email."}
            </p>
            <div className="d-flex justify-content-center">
              <button
                type="submit"
                className="itm-submit-btn"
                disabled={sendInvitationLoading}
              >
                {_t("sendInvitation") || "Send invitation"}
              </button>
            </div>
          </form>
        )}

        {/* ── WhatsApp form ── */}
        {activeTab === "whatsapp" && (
          <form onSubmit={sendInvitationViaPhone} className="itm-form">
            <div className="itm-input-row">
              <div className="forminput customInput mb-0">
                <label>{_t("tenantName")}</label>
                <input
                  type="text"
                  className="modal-input"
                  placeholder={_t("tenantName") || "Tenant name"}
                  value={sharerName}
                  required
                  onChange={(e) => setSharerName(e.target.value)}
                  onBeforeInput={(e) => {
                    const isValidChar = /^[A-Za-zÀ-ÿ\s'-]+$/.test(e.data);
                    if (!isValidChar) e.preventDefault();
                  }}
                />
              </div>
              <div className="forminput customInput mb-0">
                <label>{_t("phoneNumber")}</label>
                <PhoneInput
                  className="phone_input_info"
                  defaultCountry="es"
                  value={sharerPhone}
                  onChange={(phone) => setSharerPhone(phone)}
                  placeholder="600 000 000"
                />
              </div>
            </div>
            <div className="forminput customInput mb-0">
              <label>{_t("messageTenantOptional")}</label>
              <textarea
                className="modal-input w-100 no-textarea-focus"
                placeholder={
                  _t("messageTenantOptional") || "Message to tenant (optional)"
                }
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={4}
              />
            </div>
            <p className="itm-hint">
              {_t("messageIncludedInWhatsApp") ||
                "This message will be included in the WhatsApp message."}
            </p>
            <div className="d-flex justify-content-center">
              <button
                type="submit"
                className="itm-submit-btn"
                disabled={sendInvitationLoading}
              >
                {_t("sendInvitation") || "Send invitation"}
              </button>
            </div>
          </form>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default InviteTenantModal;
